// models/Post.js
import { Model, useRepo } from 'pinia-orm';
import Channel from '@/models/Channel.js';
import Category from '@/models/commerce/Category.js';
import Product from '@/models/commerce/Product.js';
import Media from '@/models/Media.js';
import Mediable from '@/models/Mediable.js';
import PostCategory from '@/models/PostCategory.js';

export default class Post extends Model {
  static entity = 'posts';

  static fields() {
    return {
      id: this.uid(),
      channelId: this.attr(''),
      content: this.attr(''),
      aiContent: this.attr(''),
      createdAt: this.attr(''),
      updatedAt: this.attr(''),
      publishedAt: this.attr(''),
      sourceUrl: this.attr(''),

      // Counters from API
      bookmarksCount: this.number(0),
      likesCount: this.number(0),
      commentsCount: this.number(0),
      cartItems: this.number(0),

      // User interaction flags
      isBookmarked: this.boolean(false),
      isLiked: this.boolean(false),

      // ── The two dated facts (BE→FE brief 2026-08-15, "the two-facts
      // availability contract") ────────────────────────────────────────────
      //
      // THESE MUST BE DECLARED HERE OR THEY DO NOT EXIST. pinia-orm's `$fill`
      // iterates the model's DECLARED fields, not the incoming record — any
      // wire key absent from fields() is never read, never assigned, never
      // reactive. It arrives over the wire and is silently discarded.
      //
      // `availability` — "last marked out of stock": a date, the supplier it
      // was not available at, which variants (or the whole product), and the
      // admin's optional public note. A post is the UNION of its products'
      // marks. Shape:
      //   { state:'oos', since:ISO, supplier?:string, wholeProduct:bool,
      //     variants:string[], note?:string }
      //
      // `lastCollected` — "the club last got this on `at`, from `supplier`,
      // for `price`". Independent of `availability`: a post can carry both
      // (out of stock now, and here is what it last cost), either, or neither.
      // `price` is the gross UNIT price in piasters, not a line total, and it
      // excludes Poola lines — a flash-sale price is not a repeatable price.
      //   { at:ISO, supplier?:string, price:int }
      //
      // ABSENT, NEVER NULL, on the wire. `this.attr(null)` normalises absence
      // to null here, so the client-side predicate is `!= null` — but never
      // write code that distinguishes "absent" from "explicit null": the wire
      // cannot express the difference. Read them through
      // utils/productAvailability.js, which owns every guard.
      availability: this.attr(null),
      lastCollected: this.attr(null),

      // How many OTHER members hold an out-of-stock item of this post's
      // products in a carton that can still take it (BE→FE brief
      // 2026-10-02-suggestion-reward). OMITTED when zero, so the model's null
      // means "nobody is waiting" OR "this read never carries it" — the key is
      // on the feed, the bookmarks tab and one post only; a post arriving
      // inside another read (an include, a Poola offer) never has it. Never
      // read it as proof that nobody is waiting; only ever render it when it is
      // a positive count. The viewer is never counted, and who is waiting is
      // never sent (owner G-J).
      waitingMembers: this.attr(null),

      // The GRACE leg of the feed recency gate, made visible («جديد»): the club
      // has never collected anything from this post and the post is itself
      // inside the window. OMITTED when false — `this.boolean(false)` is what
      // turns that absence into a renderable `false` rather than undefined.
      isNew: this.boolean(false),

      // ── The «تدبيسة» resale badge (BE→FE brief 2026-09-17-tadbeesa-channel-
      // posts) ──────────────────────────────────────────────────────────────
      //
      // UNLIKE `isNew`/`availability` above, these are NEVER omitted — the
      // backend always sends both, on every post (PostResource::toArray()).
      // `isResale` stays true for a listing sold down to zero (locked decision
      // 5: it is still the same real-supplier item post, just at qty 0), so it
      // is read directly rather than through a presence check.
      //
      // `resaleAvailableQty` is the live ON_SHELF piece count. The feed itself
      // drops a تدبيسة post at 0 (PostSchema::applyRecencyGate()); this app
      // still has to answer for the post's OWN page and a bookmarked read,
      // which the backend explicitly keeps answering at 0 rather than 404ing.
      isResale: this.boolean(false),
      resaleAvailableQty: this.number(0),

      // ── Her own supplier tip, and the club's answer to it (BE→FE brief
      // 2026-09-09, "she can name a shop without owning a line") ───────────
      //
      // Additive, and declared here for the same reason as the two facts
      // above: `$fill` reads the model's fields, not the wire's, so an
      // undeclared attribute is silently discarded.
      //
      // OMITTED, NEVER NULL, when she has never named a shop — the same rule
      // `availability` follows. A tip she made against an order LINE never
      // appears here: this reads line-less rows only, because the same tip
      // arriving twice under two receipts is how a member comes to believe
      // she named two shops.
      //
      // Byte-identical to `supplierSuggestion` on an order line — the same
      // backend projection — so utils/supplierSuggestion.js reads both, and a
      // member who names a shop from a post and then opens her order line
      // reads the same sentence about the same tip in both places.
      mySuggestion: this.attr(null),

      // Relationships - Pinia ORM handles these automatically
      channel: this.belongsTo(Channel, 'channelId'),
      products: this.hasMany(Product, 'postId'),
      categories: this.belongsToMany(Category, PostCategory, 'postId', 'categoryId'),
      media: this.morphToMany(
        Media,
        Mediable,
        'mediaId',
        'mediableId',
        'mediableType',
      ),//.withPivot('order', 'tag'), // OPTIMIZED: Define pivot attributes inline
    };
  }

  // OPTIMIZED: Computed getters using Pinia ORM queries for better reactivity
  get hasMedia() {
    return this.media && this.media.length > 0;
  }

  get primaryProduct() {
    return this.products?.[0] || null;
  }

  get isProduct() {
    return this.products && this.products.length > 0;
  }

  get channelName() {
    // OPTIMIZED: Use cached query for better performance
    const channel = useRepo(Channel).query().useCache().find(this.channelId);
    return channel?.name || '';
  }

  get channelAvatar() {
    const channel = useRepo(Channel).query().useCache().find(this.channelId);
    return channel?.avatarUrl || '';
  }

  get channelHandle() {
    const channel = useRepo(Channel).query().useCache().find(this.channelId);
    return channel?.username || '';
  }

  get isVerified() {
    const channel = useRepo(Channel).query().useCache().find(this.channelId);
    return channel?.verified || false;
  }

  // OPTIMIZED: Enhanced static query helpers with caching and proper constraints
  static query() {
    return super.query();
  }

  /**
   * Query with all relationships loaded
   * Uses nested with() for better performance
   */
  static withRelations() {
    return this.query()
      .with('channel')
      .with('products', (q) => q.with('category'))
      .with('media');
  }

  /**
   * Query posts ordered by creation date
   * Caches stable queries without dynamic filters
   */
  static byCreatedAt(order = 'desc') {
    return this.withRelations()
      .orderBy('createdAt', order)
      .useCache(); // Cache stable queries
  }

  /**
   * Query bookmarked posts
   * No caching as this changes frequently based on user interactions
   */
  static bookmarked() {
    return this.withRelations()
      .where('isBookmarked', true)
      .orderBy('createdAt', 'desc');
  }

  /**
   * Query posts by channel
   */
  static byChannel(channelId) {
    return this.withRelations()
      .where('channelId', channelId)
      .orderBy('createdAt', 'desc');
  }

  /**
   * OPTIMIZED: Query posts by categories using whereHas
   * Leverages relationship constraints for better performance
   */
  static byCategories(categoryIds) {
    return this.withRelations()
      .whereHas('products', (productQuery) => {
        productQuery.whereHas('category', (categoryQuery) => {
          categoryQuery.whereIn('id', categoryIds);
        });
      })
      .orderBy('createdAt', 'desc');
  }

  /**
   * OPTIMIZED: Query posts by multiple channels using whereIn
   */
  static byChannels(channelIds) {
    return this.withRelations()
      .whereIn('channelId', channelIds)
      .orderBy('createdAt', 'desc');
  }

  /**
   * OPTIMIZED: Full-text search across post content and channel name
   * Uses function-based where clause for complex conditions
   */
  static search(searchQuery) {
    const queryLower = searchQuery.toLowerCase();

    return this.withRelations()
      .where((post) => {
        // Search in post content
        if (post.content?.toLowerCase().includes(queryLower)) {
          return true;
        }

        // Search in channel name
        const channel = useRepo(Channel).find(post.channelId);
        if (channel?.name?.toLowerCase().includes(queryLower)) {
          return true;
        }

        return false;
      });
  }

  /**
   * OPTIMIZED: Query posts with products (product posts only)
   */
  static products() {
    return this.withRelations()
      .has('products')
      .orderBy('createdAt', 'desc');
  }

  /**
   * OPTIMIZED: Query posts ordered by popularity (bookmarks count)
   */
  static popular() {
    return this.withRelations()
      .orderBy('bookmarksCount', 'desc');
  }

  /**
   * OPTIMIZED: Fluent query builder for complex filters
   * Chains multiple constraints efficiently
   */
  static filtered(filters = {}) {
    let query = this.withRelations();

    // Apply category filter
    if (filters.categories?.length > 0) {
      query = query.whereHas('products', (productQuery) => {
        productQuery.whereHas('category', (categoryQuery) => {
          categoryQuery.whereIn('id', filters.categories);
        });
      });
    }

    // Apply channel filter
    if (filters.channels?.length > 0) {
      query = query.whereIn('channelId', filters.channels);
    }

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      query = query.where((post) => {
        return post.content?.toLowerCase().includes(searchLower) ||
            post.channel?.name?.toLowerCase().includes(searchLower);
      });
    }

    // Apply feed filter
    if (filters.feed === 'bookmarks') {
      query = query.where('isBookmarked', true);
    }

    if (filters.feed === 'channel' && filters.channelId) {
      query = query.where('channelId', filters.channelId);
    }

    // Apply sorting
    if (filters.sortBy === 'popular') {
      query = query.orderBy('bookmarksCount', 'desc');
    } else {
      query = query.orderBy('createdAt', 'desc');
    }

    // Cache only if no dynamic filters
    const hasDynamicFilters = filters.categories?.length > 0 ||
        filters.channels?.length > 0 ||
        filters.search;

    if (!hasDynamicFilters) {
      query = query.useCache();
    }

    return query;
  }

  static creating(model, _record) {
    return model;

  }

  // REMOVED: toggleLike() and toggleBookmark() methods
  // These are now in the usePostInteractions composable
  // This eliminates circular dependencies and follows Vue composition API patterns
}
